// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { ProfitCalculator } from '@shared/commons/cache/ProfitCalculator';
import { TradingToolBaseDataCacheTool } from '@shared/commons/cache/Tools/TradingToolBaseDataCacheTool';
import { Rectangle } from '@shared/commons/Geometry';
import { Pen, PenStyle, RectangleEdgeFilter, SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DrawPointer, DrawPointerTypeEnum } from '../../Utils/DrawPointer';
import { LayersEnum } from '../../Utils/LayersEnum';
import { Selection, SelectionState, ToolSelectionType } from '../Selection';
import { ToolView } from '../ToolView';
export class TradingToolViewBase extends ToolView {
    get isAllowOTHTradingSLTP() {
        return false;
    }
    constructor() {
        super(new TradingToolBaseDataCacheTool()); // пустышка в базовый обьект
        // #region properties
        this.iconRect = new Rectangle(0, 0, 20, 20);
        this.imageRect = Rectangle.Empty();
        this.toolIconTextRect = Rectangle.Empty();
        this.overnightRect = Rectangle.Empty();
        this.infoRect = Rectangle.Empty();
        this.leverageRect = Rectangle.Empty();
        this.actionButton = Rectangle.Empty();
        this.removeButton = Rectangle.Empty();
        this.trailingButton = Rectangle.Empty();
        this.mainContainer = Rectangle.Empty();
        // #endregion
        // To get rid of instanceof operator.
        this.isTradingTool = true;
        this.MainOrderVisibility = false;
        this.ToolViewType = TradingToolViewType.BaseToolView;
        this.priceValue = NaN;
        this.lastValidPriceValue = NaN;
        // the order was moved from its place (or an SL/TP order was created) - modification is needed
        this.TradeWasModified = false;
        this.containerHeight = 20;
        this.btnSize = 20;
        this.paddingX = 4;
        this.separatorWidth = 1;
        this.separatorHeight = 20;
    }
    get ID() {
        return '';
    }
    get Price() {
        return -1;
    }
    get PriceMin() {
        return this.Price;
    }
    get PriceMax() {
        return this.Price;
    }
    DescriptionText() {
        return '';
    }
    Updated() { }
    CancelMoving() { }
    GetCurrentSelection(x, y) {
        return ToolSelectionType.None;
    }
    ResetMouse() { }
    get Collapsed() {
        return false;
    }
    set Collapsed(value) {
        // Intentionally ignored.
    }
    // #endregion
    CreateSelection() {
        return new OrderToolSelection();
    }
    themeChanged() { }
    static ThemeChanged() {
        TradingToolViewBase.tradeFont = ThemeManager.Fonts.Font_14F_regular;
        TradingToolViewBase.chartOrderModifiedPen = new Pen(ThemeManager.colors.chartOrderModifiedBackground);
        TradingToolViewBase.chartOrderModifiedBrush = new SolidBrush(ThemeManager.colors.chartOrderModifiedBackground);
        TradingToolViewBase.chartOrderModifiedHoveredBrush = new SolidBrush(ThemeManager.colors.chartOrderModifiedBackgroundHovered);
        TradingToolViewBase.buyBrush = new SolidBrush(ThemeManager.colors.chartBuyColor);
        TradingToolViewBase.sellBrush = new SolidBrush(ThemeManager.colors.chartSellColor);
        // trades
        TradingToolViewBase.buyTradeImageActive = ThemeManager.Images.buyTradeImage;
        TradingToolViewBase.sellTradeImageActive = ThemeManager.Images.sellTradeImage;
        TradingToolViewBase.buyTradeImage = ThemeManager.Images.buyTradeImageTransparent;
        TradingToolViewBase.sellTradeImage = ThemeManager.Images.sellTradeImageTransparent;
        // background
        TradingToolViewBase.buyBackgroundActivePen = new Pen(ThemeManager.colors.buyBackgroundActiveColorPen);
        TradingToolViewBase.buyBackgroundActiveBrush = new SolidBrush(ThemeManager.colors.chartBuyTradeToolHover);
        TradingToolViewBase.sellBackgroundActivePen = new Pen(ThemeManager.colors.sellBackgroundActiveColorPen);
        TradingToolViewBase.sellBackgroundActiveBrush = new SolidBrush(ThemeManager.colors.chartSellTradeToolHover);
        TradingToolViewBase.grayBackgroundPen = new Pen(ThemeManager.colors.grayBackgroundColorPen);
        TradingToolViewBase.grayBackgroundBrush = new SolidBrush(ThemeManager.colors.grayBackgroundColor);
        TradingToolViewBase.buyBackgroundPen = new Pen(ThemeManager.colors.buyBackgroundColorPen);
        TradingToolViewBase.buyBackgroundBrush = new SolidBrush(ThemeManager.colors.chartBuyTradeToolDefault);
        TradingToolViewBase.sellBackgroundPen = new Pen(ThemeManager.colors.sellBackgroundColorPen);
        TradingToolViewBase.sellBackgroundBrush = new SolidBrush(ThemeManager.colors.chartSellTradeToolDefault);
        TradingToolViewBase.sltpChartPen = new Pen(ThemeManager.colors.sltpChartDefault);
        TradingToolViewBase.sltpChartDefaultBrush = new SolidBrush(ThemeManager.colors.sltpChartDefault);
        TradingToolViewBase.sltpChartHoverBrush = new SolidBrush(ThemeManager.colors.sltpChartHover);
        TradingToolViewBase.sellTradeToolButtonBackgroundHoveredBrush = new SolidBrush(ThemeManager.colors.chartSellTradeToolButtonBackgroundHovered);
        TradingToolViewBase.buyTradeToolButtonBackgroundHoveredBrush = new SolidBrush(ThemeManager.colors.chartBuyTradeToolButtonBackgroundHovered);
        // Alert
        TradingToolViewBase.highAlertBackgroundInactiveBrush = new SolidBrush(ThemeManager.colors.chartHighAlertInactiveBackground);
        TradingToolViewBase.highAlertBackgroundBrush = new SolidBrush(ThemeManager.colors.chartHighAlertBackground);
        TradingToolViewBase.highAlerHoveredBrush = new SolidBrush(ThemeManager.colors.chartHighAlertHoveredBackground);
        TradingToolViewBase.mediumAlertInactiveBackgroundBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertInactiveBackground);
        TradingToolViewBase.mediumAlertBackgroundBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertBackground);
        TradingToolViewBase.mediumAlertHoveredBrush = new SolidBrush(ThemeManager.colors.chartMediumAlertHoveredBackground);
        TradingToolViewBase.lowAlertInactiveBackgroundBrush = new SolidBrush(ThemeManager.colors.chartLowAlertInactiveBackground);
        TradingToolViewBase.lowAlertBackgroundBrush = new SolidBrush(ThemeManager.colors.chartLowAlertBackground);
        TradingToolViewBase.lowAlertHoveredBrush = new SolidBrush(ThemeManager.colors.chartLowAlertHoveredBackground);
        TradingToolViewBase.linkedStyleHoverBrush = new SolidBrush(ThemeManager.colors.chartTradeLinkBackgroundHovered);
        TradingToolViewBase.linkedStyleDefaultBrush = new SolidBrush(ThemeManager.colors.chartTradeLinkBackground);
        TradingToolViewBase.linkedStyleFontBrush = new SolidBrush(ThemeManager.colors.chartTradeLinkFont);
        TradingToolViewBase.linkedTradeToolButtonBackgroundHoveredBrush = new SolidBrush(ThemeManager.colors.chartLinkedTradeToolButtonBackgroundHovered);
    }
    // #region service
    /// <summary>
    /// какая точка тулзы выделена (у нас всегда 1 точка так что, а когда двигаем сл - )
    /// </summary>
    FindSelectedPoint(x, y) {
        if (this.IsSelectCheck(x, y)) {
            return 0;
        }
        return -1;
    }
    /// <summary>
    /// For futures sl/tp in currenccy
    /// </summary>
    static FuturesSlTp(order, offset) {
        let result = 0;
        const ins = order.Instrument;
        const account = order.Account;
        const Qty = order.Amount;
        const isBuy = order.BuySell === OperationType.Buy;
        const CurPriceClose = ins.roundPrice(order.Price + offset);
        const OpenPrice = order.Price;
        const crossPrice = ins.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(account, ins, order.BuySell);
        const openCross = order.isPosition ? order.OpenCrossPrice : crossPrice;
        result = ProfitCalculator.CalculateSLTP(Qty, CurPriceClose, OpenPrice, ins, account, isBuy, openCross, order.ProductType);
        return result;
    }
    // #endregion
    // #region mouse
    MinDistanceForStartMoving() {
        return 5;
    }
    /// <summary>
    /// тулза выделяется (активируется). будет ли перемещение потом - неизвестно
    /// </summary>
    ProcessActivate(x, y) {
        // мауздаун пришелся по тулзе?
        if (super.ProcessActivate(x, y)) {
            this.CurrentSelection.MouseDownHover = this.GetCurrentSelection(x, y);
            this.CurrentMovement.PrevY = y;
            return true;
        }
        // нет
        return false;
    }
    OnMouseEnter(e) {
        const newType = this.GetCurrentSelection(e.Location.X, e.Location.Y);
        if (this.CurrentSelection.CurrentHover !== newType) {
            this.CurrentSelection.CurrentHover = newType;
            e.NeedRedraw |= LayersEnum.Tools;
        }
    }
    OnMouseMove(e) {
        const newType = this.GetCurrentSelection(e.Location.X, e.Location.Y);
        if (this.CurrentSelection.CurrentHover !== newType) {
            this.CurrentSelection.CurrentHover = newType;
            e.NeedRedraw |= LayersEnum.Tools;
        }
    }
    OnMouseLeave(e) {
        if (this.CurrentSelection.CurrentHover !== ToolSelectionType.None) {
            this.CurrentSelection.CurrentHover = ToolSelectionType.None;
            e.NeedRedraw |= LayersEnum.Tools;
        }
        // this.ResetMouse();
    }
    GetCursor(e) {
        return null;
    }
    GetTooltip(e) {
        switch (this.CurrentSelection.CurrentHover) {
            case ToolSelectionType.Action:
                return Resources.getResource('TradeTool.View more actions');
            case ToolSelectionType.Trailing:
                return Resources.getResource('TradeTool.Trailing Stop');
            case ToolSelectionType.SL:
                return Resources.getResource(this.isAllowOTHTradingSLTP
                    ? 'TradeTool.Drag to set SL order with overnight execution'
                    : 'TradeTool.Drag to set SL order');
            case ToolSelectionType.TP:
                return Resources.getResource(this.isAllowOTHTradingSLTP
                    ? 'TradeTool.Drag to set TP order with overnight execution'
                    : 'TradeTool.Drag to set TP order');
            case ToolSelectionType.Line:
            case ToolSelectionType.None:
            default:
                return null;
        }
    }
    // #endregion
    Dispose() {
        this.myRenderer = null;
    }
    getVerticalSeparator(x, y) {
        const height = this.separatorHeight;
        const yPadding = this.getPadding(this.containerHeight, height);
        const separatorY = y + yPadding;
        return new Rectangle(x, separatorY, 0, height);
    }
    drawImgButton(gr, rect, defaultImage, hoveredImage, brush, roundFilter = RectangleEdgeFilter.None) {
        if (rect.IsEmpty()) {
            return;
        }
        const img = this.CurrentSelection.CurrentState !== SelectionState.None ? hoveredImage.image : defaultImage.image;
        if (!isNullOrUndefined(brush)) {
            gr.RoundRectangle(rect, 4, brush, null, roundFilter);
        }
        gr.DrawCenteredImage(img, rect);
    }
    drawTextButton(gr, rect, text, font, textBrush, rectBrush, roundFilter = RectangleEdgeFilter.None) {
        if (rect.IsEmpty()) {
            return;
        }
        if (!isNullOrUndefined(rectBrush)) {
            gr.RoundRectangle(rect, 4, rectBrush, null, roundFilter);
        }
        this.drawCenteredText(gr, text, rect, font, textBrush);
    }
    drawPriceLine(gr, containerRect, maxX, y, pen) {
        const x = containerRect.X + containerRect.Width;
        const skipDrawingLine = x > maxX - 3;
        if (!skipDrawingLine) {
            gr.DrawLine(pen, x, y, maxX, y);
        }
    }
    drawCenteredText(gr, text, rect, font, brush) {
        if (rect.IsEmpty()) {
            return;
        }
        gr.DrawString(text, font, brush, rect.X + rect.Width / 2, rect.Y + rect.Height / 2 + 1, 'center', 'middle');
    }
    tryAddPlateOnPriceScale(params, priceValue, modifiedPrice, backGroundBrush) {
        const cashItemSeriesSettings = this.myRenderer.chart.cashItemSeriesSettings;
        const instrument = this.myRenderer.Instrument;
        let price = this.TradeWasModified
            ? !isNaN(priceValue)
                ? priceValue
                : this.lastValidPriceValue
            : modifiedPrice;
        const text = instrument.formatPrice(instrument.roundPrice(price));
        price = this.ConvertPrice(price, cashItemSeriesSettings);
        params.DrawPointers.push(new DrawPointer(DrawPointerTypeEnum.VisualTrading, price, backGroundBrush, text));
    }
    addTextRectWithSeparator(gr, text, curX, curY, separators, setRect) {
        if (!isValidString(text)) {
            return curX;
        }
        const width = Math.floor(gr.GetTextWidth(text, TradingToolViewBase.tradeFont)) + this.paddingX * 2;
        return this.addRectWithSeparator(curX, curY, width, separators, setRect);
    }
    addRectWithSeparator(curX, curY, width, separators, setRect) {
        curX += this.separatorWidth;
        separators.push(this.getVerticalSeparator(curX, curY));
        curX += this.separatorWidth;
        const rect = new Rectangle(curX, curY, width, this.containerHeight);
        setRect(rect);
        return curX + width;
    }
    drawSeparators(gr, separators) {
        const pen = new Pen(ThemeManager.colors.chartTradeToolSeparator, 1, PenStyle.SimpleChart);
        for (const separator of separators) {
            gr.DrawLine(pen, separator.X, separator.Y, separator.X + separator.Width, separator.Y + separator.Height);
        }
    }
    getPadding(parentSize, childSize) {
        const padding = Math.floor((parentSize - childSize) / 2);
        return padding < 0 ? 0 : padding;
    }
}
export var TradingToolViewType;
(function (TradingToolViewType) {
    TradingToolViewType[TradingToolViewType["BaseToolView"] = 0] = "BaseToolView";
    TradingToolViewType[TradingToolViewType["OrderToolView"] = 1] = "OrderToolView";
    TradingToolViewType[TradingToolViewType["PositionToolView"] = 2] = "PositionToolView";
    TradingToolViewType[TradingToolViewType["SLTPOrderToolView"] = 3] = "SLTPOrderToolView";
    TradingToolViewType[TradingToolViewType["NewTradeToolView"] = 4] = "NewTradeToolView";
    TradingToolViewType[TradingToolViewType["StopLimitOrderToolView"] = 5] = "StopLimitOrderToolView";
    TradingToolViewType[TradingToolViewType["AlertToolView"] = 6] = "AlertToolView";
    TradingToolViewType[TradingToolViewType["StopLossLimitToolView"] = 7] = "StopLossLimitToolView";
    TradingToolViewType[TradingToolViewType["NewPlacingOrderToolView"] = 8] = "NewPlacingOrderToolView";
})(TradingToolViewType || (TradingToolViewType = {}));
class OrderToolSelection extends Selection {
    constructor() {
        super(...arguments);
        this.MouseDownHover = ToolSelectionType.None;
        this.CurrentHover = ToolSelectionType.None;
    }
}
